// frontend/src/utils/dateTimeFormatter.js
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const DATE_FORMAT_PATTERNS = {
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd'
};

const resolveDatePattern = (dateFormat) => (
  DATE_FORMAT_PATTERNS[dateFormat] || DATE_FORMAT_PATTERNS['MM/DD/YYYY']
);

const resolveTimePattern = (timeFormat) => (
  timeFormat === '24h' ? 'HH:mm' : 'h:mm a'
);

// Get user preferences from localStorage - using centralized userPreferences object
const getUserPreferences = () => {
  try {
    // Read from centralized userPreferences object
    const storedPrefs = localStorage.getItem('userPreferences');
    if (storedPrefs) {
      const prefs = JSON.parse(storedPrefs);
      return {
        dateFormat: prefs.date_format || 'MM/DD/YYYY',
        timeFormat: prefs.time_format || '12h'
      };
    }
    
    // Fallback to individual keys for backward compatibility (will be removed later)
    return {
      dateFormat: localStorage.getItem('dateFormat') || 'MM/DD/YYYY',
      timeFormat: localStorage.getItem('timeFormat') || '12h'
    };
  } catch (error) {
    console.error('Failed to read user preferences from localStorage', { error: error.message });
    return {
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h'
    };
  }
};

const parseDateInput = (value) => {
  if (!value) return value;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return value;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/.test(value)) {
    const [datePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return parseISO(value);
};

const parseInstantInput = (value) => {
  if (value instanceof Date) return value;
  if (
    typeof value === 'string'
    && /T.+(?:Z|[+-]\d{2}:\d{2})$/i.test(value)
  ) {
    return parseISO(value);
  }

  // A date-only or offset-less datetime does not identify one instant. Refuse
  // to reinterpret it through the browser timezone when an explicit display
  // timezone was requested.
  throw new RangeError('Timezone-aware formatting requires an offset-bearing instant');
};

/**
 * Backend timestamps represent UTC instants even when a provider supplied the
 * legacy offset-less MySQL shape. Normalize that shape before any arithmetic;
 * appending `Z` only at the final formatter would leave variance calculations
 * browser-dependent. Explicit `Z` and `±HH:mm` offsets are preserved.
 */
export const parseBackendUtcInstant = (value) => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const trimmed = value.trim();
  let normalized = null;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed)) {
    normalized = `${trimmed.replace(' ', 'T')}Z`;
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/i.test(trimmed)) {
    normalized = trimmed;
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed)) {
    normalized = `${trimmed}Z`;
  }

  return normalized ? parseISO(normalized) : new Date(NaN);
};

export const isSameDayInTimeZone = (left, right, timeZone) => {
  if (!(left instanceof Date) || !(right instanceof Date)) return false;
  if (Number.isNaN(left.getTime()) || Number.isNaN(right.getTime())) return false;

  try {
    return formatInTimeZone(left, timeZone, 'yyyy-MM-dd')
      === formatInTimeZone(right, timeZone, 'yyyy-MM-dd');
  } catch {
    return false;
  }
};

const isTimeOnlyString = (value) => (
  typeof value === 'string' &&
  /^\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value)
);

const isSameLocalDate = (a, b) => (
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()
);

export const parseLocalDate = parseDateInput;

export const formatDateLocal = (value, customFormat = null) => {
  if (!value) return '';

  const dateObj = parseLocalDate(value);
  if (!dateObj || Number.isNaN(dateObj.getTime())) return '';

  return formatDate(dateObj, customFormat);
};

// Short date-only display for compact UI labels that should not include time.
export const formatDateShort = (value) => {
  if (!value) return '';

  const dateObj = parseLocalDate(value);
  if (!dateObj || Number.isNaN(dateObj.getTime())) return '';

  try {
    return format(dateObj, 'MMM d, yyyy');
  } catch (error) {
    console.error('Short date formatting failed', { error: error.message, value });
    return value.toString();
  }
};

// Format date according to user preference
export const formatDate = (date, customFormat = null) => {
  const preferences = getUserPreferences();
  const dateFormat = customFormat || preferences.dateFormat || 'MM/DD/YYYY';
  
  if (!date) return '';
  
  const dateObj = parseDateInput(date);
  
  try {
    return format(dateObj, resolveDatePattern(dateFormat));
  } catch (error) {
    console.error('Date formatting failed', { error: error.message, date, customFormat });
    return date.toString();
  }
};

// Format time according to user preference
export const formatTime = (date, customFormat = null, timeZone = null) => {
  const preferences = getUserPreferences();
  const timeFormat = customFormat || preferences.timeFormat || '12h';
  
  if (!date) return '';
  
  try {
    const dateObj = timeZone ? parseInstantInput(date) : parseDateInput(date);
    return timeZone
      ? formatInTimeZone(dateObj, timeZone, resolveTimePattern(timeFormat))
      : format(dateObj, resolveTimePattern(timeFormat));
  } catch (error) {
    console.error('Time formatting failed', { error: error.message, date, customFormat, timeZone });
    return date.toString();
  }
};

// Format date and time together
export const formatDateTime = (
  date,
  customDateFormat = null,
  customTimeFormat = null,
  timeZone = null
) => {
  const preferences = getUserPreferences();
  const dateFormat = customDateFormat || preferences.dateFormat || 'MM/DD/YYYY';
  const timeFormat = customTimeFormat || preferences.timeFormat || '12h';
  
  if (!date) return '';

  try {
    // The local-date parser intentionally protects date-only values from UTC
    // shifts. Entity-zoned displays instead require the original instant so a
    // real UTC-midnight block can correctly render on the prior local date.
    const dateObj = timeZone ? parseInstantInput(date) : parseDateInput(date);
    const formatPart = (pattern) => (
      timeZone
        ? formatInTimeZone(dateObj, timeZone, pattern)
        : format(dateObj, pattern)
    );
    const datePart = formatPart(resolveDatePattern(dateFormat));
    const timePart = formatPart(resolveTimePattern(timeFormat));
    return `${datePart} ${timePart}`;
  } catch (error) {
    console.error('DateTime formatting failed', {
      error: error.message,
      date,
      customDateFormat,
      customTimeFormat,
      timeZone
    });
    return date.toString();
  }
};

// Format an instant range with one date when both endpoints share the same
// calendar date in the requested timezone. Offset changes are shown explicitly
// because repeated wall times during DST fall-back would otherwise look like a
// zero-length block.
export const formatDateTimeRange = (
  start,
  end,
  customDateFormat = null,
  customTimeFormat = null,
  timeZone = null
) => {
  const startDisplay = formatDateTime(
    start,
    customDateFormat,
    customTimeFormat,
    timeZone
  );
  const endDisplay = formatDateTime(
    end,
    customDateFormat,
    customTimeFormat,
    timeZone
  );

  const joinFormattedEndpoints = () => {
    if (startDisplay && endDisplay) return `${startDisplay} – ${endDisplay}`;
    return startDisplay || endDisplay || '';
  };

  if (!startDisplay || !endDisplay) return joinFormattedEndpoints();

  try {
    const preferences = getUserPreferences();
    const dateFormat = customDateFormat || preferences.dateFormat || 'MM/DD/YYYY';
    const timeFormat = customTimeFormat || preferences.timeFormat || '12h';
    const startDate = timeZone ? parseInstantInput(start) : parseDateInput(start);
    const endDate = timeZone ? parseInstantInput(end) : parseDateInput(end);
    if (
      !startDate
      || !endDate
      || Number.isNaN(startDate.getTime())
      || Number.isNaN(endDate.getTime())
    ) {
      return joinFormattedEndpoints();
    }

    const formatPart = (date, pattern) => (
      timeZone
        ? formatInTimeZone(date, timeZone, pattern)
        : format(date, pattern)
    );
    const sameDay = timeZone
      ? isSameDayInTimeZone(startDate, endDate, timeZone)
      : isSameLocalDate(startDate, endDate);
    if (!sameDay) return joinFormattedEndpoints();

    const datePart = formatPart(startDate, resolveDatePattern(dateFormat));
    const startTime = formatPart(startDate, resolveTimePattern(timeFormat));
    const endTime = formatPart(endDate, resolveTimePattern(timeFormat));
    const startOffset = formatPart(startDate, 'xxx');
    const endOffset = formatPart(endDate, 'xxx');

    if (startOffset !== endOffset) {
      return `${datePart} ${startTime} ${startOffset} – ${endTime} ${endOffset}`;
    }
    return `${datePart} ${startTime} – ${endTime}`;
  } catch {
    // The existing endpoint formatter owns its safe fallback behavior. Range
    // compaction must never turn malformed data into a render-time exception.
    return joinFormattedEndpoints();
  }
};

// Format a datetime as time-only for today, or date + time for other days.
export const formatDateAwareTime = (
  value,
  customDateFormat = null,
  customTimeFormat = null,
  referenceDate = new Date()
) => {
  if (!value) return '';

  if (isTimeOnlyString(value)) {
    return formatTimeString(value, customTimeFormat);
  }

  const dateObj = parseDateInput(value);
  const referenceObj = parseDateInput(referenceDate);
  if (
    !dateObj ||
    !referenceObj ||
    Number.isNaN(dateObj.getTime()) ||
    Number.isNaN(referenceObj.getTime())
  ) {
    return '';
  }

  if (isSameLocalDate(dateObj, referenceObj)) {
    return formatTime(dateObj, customTimeFormat);
  }

  return formatDateTime(dateObj, customDateFormat, customTimeFormat);
};

// Format date for short display (like "Jan 5, 14:30" or "Jan 5, 2:30 PM")
export const formatDateTimeShort = (date) => {
  const preferences = getUserPreferences();
  const timeFormat = preferences.timeFormat || '12h';
  
  if (!date) return '';
  
  const dateObj = parseDateInput(date);
  
  try {
    if (timeFormat === '24h') {
      return format(dateObj, 'MMM d, HH:mm');
    } else {
      return format(dateObj, 'MMM d, h:mm a');
    }
  } catch (error) {
    console.error('Short datetime formatting failed', { error: error.message, date });
    return date.toString();
  }
};

// Format date to ISO format (YYYY-MM-DD) for API calls
export const formatDateISO = (date) => {
  if (!date) return '';
  
  const dateObj = parseDateInput(date);
  
  try {
    return format(dateObj, 'yyyy-MM-dd');
  } catch (error) {
    console.error('ISO date formatting failed', { error: error.message, date });
    return date.toString();
  }
};

// Parse time string (HH:mm) to display format
export const formatTimeString = (timeString, customFormat = null) => {
  if (!timeString) return '';
  
  const preferences = getUserPreferences();
  const timeFormat = customFormat || preferences.timeFormat || '12h';
  
  try {
    // A time-only value is a wall-clock label, not an instant. Formatting it
    // arithmetically avoids browser timezone and DST gap reinterpretation.
    const match = String(timeString).match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
    if (!match) return timeString;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return timeString;
    if (timeFormat === '24h') {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    } else {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours % 12 || 12;
      return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
    }
  } catch (error) {
    console.error('Time string formatting failed', { error: error.message, timeString });
    return timeString;
  }
};

// Listen for preference updates and clear any cached values
if (typeof window !== 'undefined') {
  window.addEventListener('preferencesUpdated', (event) => {
    // Only log preference updates if user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      // When preferences are updated, the new values are already in localStorage
      // Components using these formatters will get the updated values on next call
      console.log('Date/time preferences updated', { preferences: event.detail });
    }
  });
}
